import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogPopup,
  DialogTitle,
  Icon,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Text,
  useIsMobile,
  useWidgetRegistry,
  type IconName,
  type Color,
  type WidgetPreset,
} from "@salestrak/ui";
import { TilePreview } from "#app/widgets/dashboards/settings/tile-preview";
import { widgetTagLabel } from "#app/widgets/dashboards/widget-tags";
import type { Omittable } from "@salestrak/ui";
import { at } from "#app/lib/utils/at";

const ACTIVATION_OFFSET = 24;

/** Preset tiles: one column on phones, three from `sm` up. The library ships
 *  no grid primitive, so the layout is a class. */
const PRESET_GRID = "grid grid-cols-1 sm:grid-cols-3";

/** The gallery card's hover lift — Box carries no hover-border or transition
 *  props. */
const PRESET_CARD_HOVER =
  "text-start transition-colors hover:border-foreground/32";

/** Near-fullscreen browser surface: `min(1177px, 100vw - 2rem)` wide with no
 *  popup padding. The explicit height collapses the viewport's fr rows so the
 *  two-pane layout gets a real box to fill. */
const GALLERY_POPUP =
  "h-[min(760px,calc(100dvh-4rem))] w-[calc(100vw-2rem)] max-w-[1177px] p-0";

/** The Custom gallery mixes hues per entry, ClickUp-style; every other section stays on its own color. */
const CUSTOM_PRESET_HUES: readonly Color[] = [
  "violet",
  "orange",
  "teal",
  "blue",
  "pink",
  "yellow",
  "green",
  "red",
  "light-blue",
];

export type AddWidgetDialogProps = Omittable<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (preset: WidgetPreset) => void;
  /** Sidebar header label — the same gallery doubles as the Replace picker
   *  (the dashboard host swaps the copy while a replace is pending). */
  title?: string;
}>;

type GallerySectionKey =
  | "featured"
  | "custom"
  | "stages"
  | "assignee"
  | "table"
  | "competition"
  | "pipeline"
  | "coaching"
  | "activity"
  | "embeds";

/**
 * A gallery section resolves its widgets ONE of two ways:
 * - `presetKeys` — an explicit, ordered allowlist of catalog keys. This is the
 *   default: the curated sections are hand-picked and ordered, and the allowlist
 *   keeps any exploratory presets OUT of the gallery until they're promoted to
 *   a real preset key here.
 * - `match` — a predicate over every registered preset. Used only for Embeds, so
 *   any future embed preset is picked up automatically.
 * Keys that don't resolve to a registered preset are silently skipped, so a
 * section can name a widget that isn't shipped yet without breaking the gallery.
 */
type GallerySection = Omittable<{
  key: GallerySectionKey;
  label: string;
  description: string;
  icon: IconName;
  color: Color;
  categoryLabel: string;
  presetKeys?: readonly string[];
  match?: (preset: WidgetPreset) => boolean;
}>;

const GALLERY_SECTIONS: GallerySection[] = [
  {
    key: "featured",
    label: "Featured",
    description: "A starter set that shows the range — a pie, a trend, a list, a metric.",
    icon: "star",
    color: "orange",
    categoryLabel: "Recommended",
    presetKeys: ["type-mix.stage", "series-line.stage", "entity-list.projects", "metric.unassigned"],
  },
  {
    key: "custom",
    label: "Custom",
    description: "Start from a baseline chart type and configure it yourself.",
    icon: "sliders-horizontal",
    color: "light-blue",
    categoryLabel: "Charts",
    presetKeys: [
      "series-line.custom",
      "dimension-bar.custom",
      "type-mix.pie",
      "type-mix.donut",
      "dimension-battery.custom",
      "portfolio-map.treemap",
      "metric.custom",
      "note.default",
      "section-header.default",
    ],
  },
  {
    key: "stages",
    label: "Stages",
    description: "Workload, movement, and time across your pipeline stages.",
    icon: "layers",
    color: "violet",
    categoryLabel: "Charts",
    presetKeys: [
      "type-mix.stage",
      "dimension-battery.stage",
      "dimension-bar.stage",
      "series-line.stage",
      "stage-age.stage",
      "portfolio-map.workflow-map",
    ],
  },
  {
    key: "assignee",
    label: "Assignee",
    description: "Break projects down by who owns them, and spot the unassigned.",
    icon: "user",
    color: "green",
    categoryLabel: "Charts",
    presetKeys: ["type-mix.assignee", "dimension-battery.assignee", "dimension-bar.assignee", "metric.unassigned"],
  },
  {
    key: "table",
    label: "Table",
    description: "The projects and activity in scope, listed row by row.",
    icon: "table",
    color: "blue",
    categoryLabel: "Tables",
    presetKeys: ["entity-list.projects", "activity-list.default"],
  },
  {
    key: "competition",
    label: "Competition",
    description: "Rank output and movement so teams can see who is setting the pace.",
    icon: "trophy",
    color: "yellow",
    categoryLabel: "Playbooks",
    presetKeys: [
      "dimension-bar.leaderboard",
      "projects-closed.default",
      "most-active-users.default",
      "stage-moves.byStage",
    ],
  },
  {
    key: "pipeline",
    label: "Pipeline",
    description: "Track where work sits, how it moves, and who owns the load.",
    icon: "git-branch",
    color: "teal",
    categoryLabel: "Playbooks",
    presetKeys: ["type-mix.pipeline", "stage-moves.byStage", "stage-age.stage", "dimension-bar.assignee"],
  },
  {
    key: "coaching",
    label: "Coaching",
    description: "Find stalled work, coach follow-through, and keep ownership clear.",
    icon: "target",
    color: "red",
    categoryLabel: "Playbooks",
    presetKeys: ["entity-list.needs-attention", "entity-list.stale", "stage-throughput.default", "metric.unassigned"],
  },
  {
    key: "activity",
    label: "Activity",
    description: "See the cadence of updates and the latest project activity.",
    icon: "activity",
    color: "pink",
    categoryLabel: "Playbooks",
    presetKeys: ["activity-heatmap.default", "activity-heatmap.workflow-activity", "activity-list.default"],
  },
  {
    key: "embeds",
    label: "Embeds & apps",
    description: "Bring external pages and videos onto the board.",
    icon: "globe",
    color: "grey",
    categoryLabel: "External",
    match: (preset) => preset.type === "embed",
  },
];

/**
 * One gallery entry: a full-bleed gradient preview (dark mini-window mockup
 * per chart family) over a name + description footer. Pressing it adds the
 * widget.
 */
function PresetEntry({
  preset,
  color,
  onSelect,
  compact = false,
}: Omittable<{
  preset: WidgetPreset;
  color: Color;
  onSelect: (preset: WidgetPreset) => void;
  compact?: boolean;
}>) {
  return (
    // A pressable tile is a real button element, not a Card — the library Card
    // carries no press/hover-border affordance.
    <Box
      as="button"
      direction="column"
      border
      rounded="lg"
      overflow="hidden"
      cursor="pointer"
      className={PRESET_CARD_HOVER}
      onClick={() => onSelect(preset)}
    >
      {/* Relative: the mockup positions absolutely inside this frame. */}
      <Box
        pointerEvents="none" height={compact ? 112 : 136}
        overflow="hidden"
        position="relative"
        fill="width"
      >
        <TilePreview preset={preset} color={color} />
      </Box>
      <Box
        direction="column"
        gap="2xs"
        fill="width"
        paddingX={compact ? "md" : "lg"}
        paddingY="md"
      >
        <Text size="sm" weight="semibold" truncate={1}>
          {preset.label}
        </Text>
        <Text size="xs" variant="muted" truncate={2}>
          {preset.description}
        </Text>
      </Box>
    </Box>
  );
}

/**
 * The gallery pane: a scroll-spy sidebar of curated sections beside preset
 * tiles laid out in a three-column grid, filtered by a search that matches
 * label, description, section, and tags. Empty sections are pruned. Widgets
 * are authored in code; this is the single way to add one to a view.
 */
function GalleryTab({
  sidebarTitle,
  onSelect,
  onClose,
}: {
  sidebarTitle: string;
  onSelect: (preset: WidgetPreset) => void;
  onClose: () => void;
}) {
  const isMobile = useIsMobile();
  const registry = useWidgetRegistry();
  const [query, setQuery] = useState("");
  const [activeKey, setActiveKey] = useState<GallerySectionKey>("featured");
  const scrollNodeRef = useRef<HTMLDivElement | null>(null);
  const sectionNodes = useRef<Map<GallerySectionKey, HTMLElement>>(new Map());
  const sectionRefSetters = useRef<Map<GallerySectionKey, (node: unknown) => void>>(new Map());
  const lockRef = useRef(false);
  const lockTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  const activeSection =
    GALLERY_SECTIONS.find((section) => section.key === activeKey) ??
    at(GALLERY_SECTIONS, 0, "gallery section");

  const visibleSections = useMemo(() => {
    const presets = registry.getPresets();
    // Resolve the ordered allowlists against what's actually registered — a key
    // with no live preset (renamed/unshipped widget) is skipped, not an error.
    const byKey = new Map(presets.map((p) => [p.key, p]));
    const q = query.trim().toLowerCase();
    const matchesQuery = (p: WidgetPreset) =>
      !q ||
      p.label.toLowerCase().includes(q) ||
      p.description.toLowerCase().includes(q) ||
      p.section.toLowerCase().includes(q) ||
      (p.tags ?? []).some((t) => (widgetTagLabel(t) ?? t).toLowerCase().includes(q));
    return GALLERY_SECTIONS.map((section) => {
      const inSection = section.presetKeys
        ? section.presetKeys.map((key) => byKey.get(key)).filter((p): p is WidgetPreset => Boolean(p))
        : presets.filter((preset) => section.match?.(preset) ?? false);
      return { section, presets: inSection.filter(matchesQuery) };
    }).filter(({ presets }) => presets.length > 0);
  }, [registry, query]);

  const getSectionRef = useCallback((key: GallerySectionKey) => {
    const cache = sectionRefSetters.current;
    let setter = cache.get(key);
    if (!setter) {
      setter = (node: unknown) => {
        const el = node as HTMLElement | null;
        if (el) sectionNodes.current.set(key, el);
        else sectionNodes.current.delete(key);
      };
      cache.set(key, setter);
    }
    return setter;
  }, []);

  const syncActiveSection = useCallback(() => {
    const container = scrollNodeRef.current;
    if (!container || lockRef.current) return;

    if (container.scrollTop + container.clientHeight >= container.scrollHeight - 2) {
      for (let i = GALLERY_SECTIONS.length - 1; i >= 0; i--) {
        const key = at(GALLERY_SECTIONS, i).key;
        if (sectionNodes.current.has(key)) {
          setActiveKey(key);
          return;
        }
      }
      return;
    }

    const containerTop = container.getBoundingClientRect().top;
    let current =
      visibleSections[0]?.section.key ?? at(GALLERY_SECTIONS, 0, "gallery section").key;
    for (const { section } of visibleSections) {
      const el = sectionNodes.current.get(section.key);
      if (!el) continue;
      if (el.getBoundingClientRect().top <= containerTop + ACTIVATION_OFFSET) {
        current = section.key;
      }
    }
    setActiveKey(current);
  }, [visibleSections]);

  const setScrollNode = useCallback(
    (node: HTMLDivElement | null) => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
      scrollNodeRef.current = node;
      if (!node) return;

      let raf = 0;
      const onScroll = () => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          syncActiveSection();
        });
      };

      node.addEventListener("scroll", onScroll, { passive: true });
      cleanupRef.current = () => {
        node.removeEventListener("scroll", onScroll);
        if (raf) cancelAnimationFrame(raf);
      };
    },
    [syncActiveSection],
  );

  const scrollToSection = useCallback(
    (key: GallerySectionKey) => {
      setActiveKey(key);
      const container = scrollNodeRef.current;
      const el = sectionNodes.current.get(key);
      if (!container || !el) return;

      const top = el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
      lockRef.current = true;
      if (lockTimer.current) clearTimeout(lockTimer.current);
      lockTimer.current = setTimeout(() => {
        lockRef.current = false;
        syncActiveSection();
      }, 450);
      container.scrollTo({ top, behavior: "smooth" });
    },
    [syncActiveSection],
  );

  useEffect(() => {
    if (visibleSections.some(({ section }) => section.key === activeKey)) return;
    setActiveKey(
      visibleSections[0]?.section.key ?? at(GALLERY_SECTIONS, 0, "gallery section").key,
    );
  }, [activeKey, visibleSections]);

  useEffect(
    () => () => {
      if (cleanupRef.current) cleanupRef.current();
      if (lockTimer.current) clearTimeout(lockTimer.current);
    },
    [],
  );

  return (
    <Box direction="row" fill="flex">
      <Box
        direction="column"
        width={isMobile ? 176 : 256}
        shrink={false}
        surface="sunken"
        border="right"
      >
        <Box height={48} paddingX="md" justify="center">
          <Text size="sm" weight="medium">
            {sidebarTitle}
          </Text>
        </Box>
        {/* Plain overflow, not ScrollArea: this nav column is short and the
            scrollbar is not chrome anyone reads here. */}
        <Box fill="flex" overflow="auto">
          <Box direction="column" paddingX="sm" paddingBottom="md" gap="2xs">
            {/* `SidebarMenuButton` calls `useSidebar()` and THROWS outside a
                `SidebarProvider`. This gallery nav column is
                inside a dialog, not a sidebar, so its rows are plain ghost
                buttons carrying the active wash themselves. */}
            {GALLERY_SECTIONS.map((section) => (
              <Button
                key={section.key}
                variant={section.key === activeKey ? "secondary" : "ghost"}
                size="sm"
                className="w-full justify-start"
                onClick={() => scrollToSection(section.key)}
              >
                <Icon name={section.icon} size="sm" />
                <Text size="sm" truncate={1}>
                  {section.label}
                </Text>
              </Button>
            ))}
          </Box>
        </Box>
      </Box>

      <Box direction="column" fill="flex">
        <Box
          direction="row"
          align="center"
          gap="sm"
          height={48}
          paddingX={isMobile ? "sm" : "md"}
          border="bottom"
          shrink={false}
        >
          <Box direction="row" fill="flex" align="center" gap="sm">
            <Icon name={activeSection.icon} size="sm" color="muted" />
            <Text size="sm" weight="semibold" truncate={1}>
              {activeSection.label}
            </Text>
          </Box>
          <Button
            variant="secondary"
            size="icon-xs"
            className="rounded-full"
            aria-label="Close"
            onClick={onClose}
          >
            <Icon name="x" size="xs" />
          </Button>
        </Box>
        <Box
          direction="row"
          align="center"
          paddingX={isMobile ? "sm" : "md"}
          paddingY="sm"
          shrink={false}
        >
          <Box fill="flex" shrink maxWidth={isMobile ? undefined : 320}>
            <InputGroup>
              <InputGroupAddon>
                <Icon name="search" />
              </InputGroupAddon>
              <InputGroupInput
                type="search"
                placeholder="Search widgets…"
                value={query}
                onValueChange={setQuery}
              />
            </InputGroup>
          </Box>
        </Box>
        {/* Plain overflow with a ref, not ScrollArea: the scroll-spy reads
            `scrollTop` off this element and `scrollToSection` scrolls it
            directly. */}
        <Box ref={setScrollNode} fill="flex" overflow="auto">
          <Box
            direction="column"
            gap="lg"
            paddingX={isMobile ? "sm" : "md"}
            paddingTop="md"
            paddingBottom="xl"
          >
            {visibleSections.length === 0 ? (
              <Box direction="column" align="center" gap="2xs" paddingY="2xl">
                <Icon name="search" size="lg" color="muted" />
                <Text size="sm" weight="medium">
                  No widgets found
                </Text>
                <Text size="sm" variant="muted">
                  {query ? `Nothing matches "${query}"` : "There are no widgets to add yet"}
                </Text>
              </Box>
            ) : null}
            {visibleSections.map(({ section, presets }) => (
              <Box
                key={section.key}
                direction="column"
                ref={getSectionRef(section.key)}
                gap="md"
              >
                <Box
                  direction="column"
                  gap="2xs"
                  paddingRight={isMobile ? undefined : "2xl"}
                >
                  <Text size={isMobile ? "base" : "lg"} weight="semibold">
                    {section.label}
                  </Text>
                  <Text size="sm" variant="muted">
                    {section.description}
                  </Text>
                </Box>
                <Box direction="column" gap={isMobile ? "xs" : "sm"}>
                  <Text size="xs" weight="semibold" variant="muted">
                    {section.categoryLabel.toUpperCase()}
                  </Text>
                  <Box gap={isMobile ? "sm" : "md"} className={PRESET_GRID}>
                    {presets.map((preset, index) => (
                      <PresetEntry
                        key={`${section.key}-${preset.key}`}
                        preset={preset}
                        color={
                          section.key === "custom"
                            ? at(CUSTOM_PRESET_HUES, index % CUSTOM_PRESET_HUES.length)
                            : section.color
                        }
                        onSelect={onSelect}
                        compact={isMobile}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

/**
 * Add Widget — the curated preset gallery. Widgets are authored in code; this
 * is the single way to add one to a view.
 */
export function AddWidgetDialog({
  open,
  onOpenChange,
  onSelect,
  title = "Add Widget",
}: AddWidgetDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup showCloseButton={false} className={GALLERY_POPUP}>
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <GalleryTab
          sidebarTitle={title}
          onSelect={(p) => {
            onSelect(p);
            onOpenChange(false);
          }}
          onClose={() => onOpenChange(false)}
        />
      </DialogPopup>
    </Dialog>
  );
}
